import api from '@/api/commissions'
import balanceApi from '@/api/balance'

const getDefaultState = () => ({
  card: '',
  cardNotFound: false,
  amount: 0,
  balance: 0,
  balanceLoaded: false,
  loadingBalance: false,
  cardExpirationMonth: '',
  cardExpirationYear: '',
  cardHolderFirstName: '',
  cardHolderLastName: '',
  cardHolderEmail: '',
  cardHolderStreet: '',
  cardHolderCity: '',
  cardHolderState: '',
  cardHolderCountry: '',
  cardHolderPhone: '',
  cardHolderZipCode: '',
  cardNumber: '',
  cardType: 0,
  commission: {},
  commissionType: '',
  commissionAmount: '',
  commissionBrand: ''
})

const state = getDefaultState()

const mutations = {
  CLEAR_STATE: (state, value) => {
    Object.assign(state, getDefaultState())
  },
  SET_CARD: (state, value) => {
    state.card = value
  },
  SET_AMOUNT: (state, value) => {
    state.amount = value
  },
  SET_CARD_EXPIRATION_MONTH: (state, value) => {
    state.cardExpirationMonth = value
  },
  SET_CARD_EXPIRATION_YEAR: (state, value) => {
    state.cardExpirationYear = value
  },
  SET_CARD_HOLDER_FIRST_NAME: (state, value) => {
    state.cardHolderFirstName = value
  },
  SET_CARD_HOLDER_LAST_NAME: (state, value) => {
    state.cardHolderLastName = value
  },
  SET_CARD_HOLDER_EMAIL: (state, value) => {
    state.cardHolderEmail = value
  },
  SET_CARD_HOLDER_PHONE: (state, value) => {
    state.cardHolderPhone = value.replace(/\D/g, '')
  },
  SET_CARD_HOLDER_STREET: (state, value) => {
    state.cardHolderStreet = value
  },
  SET_CARD_HOLDER_CITY: (state, value) => {
    state.cardHolderCity = value
  },
  SET_CARD_HOLDER_STATE: (state, value) => {
    state.cardHolderState = value
  },
  SET_CARD_HOLDER_COUNTRY: (state, value) => {
    state.cardHolderCountry = value
  },
  SET_CARD_HOLDER_ZIPCODE: (state, value) => {
    state.cardHolderZipCode = value
  },
  SET_CARD_NUMBER: (state, value) => {
    state.cardNumber = value
  },
  SET_CARD_TYPE: (state, value) => {
    state.cardType = value
  },
  SET_COMMISSION: (state, value) => {
    state.commissionType = value.type
    state.commissionAmount = value.amount
    state.commissionBrand = value.brand
  },
  SET_BALANCE_LOADING: (state) => {
    state.loadingBalance = true
  },
  SET_BALANCE_RESULT: (state, { balance, cardNotFound }) => {
    state.balance = balance
    state.cardNotFound = cardNotFound
    state.loadingBalance = false
    state.balanceLoaded = true
  }
}

const actions = {
  clearRechargeFormState ({ commit }) {
    commit('CLEAR_STATE')
  },
  resetCommission ({ commit }) {
    commit('SET_COMMISSION', getDefaultState().commission)
  },
  resetBalance ({ commit }) {
    commit('SET_BALANCE_RESULT', {
      balance: getDefaultState().balance,
      balanceLoaded: getDefaultState().balanceLoaded,
      cardNotFound: getDefaultState().cardNotFound
    })
  },
  updateCard ({ commit }, value) {
    commit('SET_CARD', value)
  },
  updateAmount ({ commit }, value) {
    if (!isNaN(value) && value) {
      return commit('SET_AMOUNT', parseFloat(value))
    }

    return commit('SET_AMOUNT', 0)
  },
  updateCardExpirationMonth ({ commit }, value) {
    commit('SET_CARD_EXPIRATION_MONTH', value)
  },
  updateCardExpirationYear ({ commit }, value) {
    commit('SET_CARD_EXPIRATION_YEAR', value)
  },
  updateCardHolderFirstName ({ commit }, value) {
    commit('SET_CARD_HOLDER_FIRST_NAME', value)
  },
  updateCardHolderLastName ({ commit }, value) {
    commit('SET_CARD_HOLDER_LAST_NAME', value)
  },
  updateCardHolderEmail ({ commit }, value) {
    commit('SET_CARD_HOLDER_EMAIL', value)
  },
  updateCardHolderPhone ({ commit }, value) {
    commit('SET_CARD_HOLDER_PHONE', value)
  },
  updateCardHolderStreet ({ commit }, value) {
    commit('SET_CARD_HOLDER_STREET', value)
  },
  updateCardHolderCity ({ commit }, value) {
    commit('SET_CARD_HOLDER_CITY', value)
  },
  updateCardHolderZipCode ({ commit }, value) {
    commit('SET_CARD_HOLDER_ZIPCODE', value)
  },
  updateCardHolderState ({ commit }, value) {
    commit('SET_CARD_HOLDER_STATE', value)
  },
  updateCardHolderCountry ({ commit }, value) {
    commit('SET_CARD_HOLDER_COUNTRY', value)
  },
  updateCardNumber ({ commit, dispatch, state }, value) {
    commit('SET_CARD_NUMBER', value)
  },
  updateCardType ({ commit }, value) {
    commit('SET_CARD_TYPE', value)
  },
  async getCommission ({ commit, state }) {
    try {
      const { data } = await api.v2.getCardCommission(state.cardNumber)
      const { amount, brand, type } = data

      commit('SET_COMMISSION', {
        amount: parseFloat(amount),
        brand,
        type
      })
    } catch (error) {
    }
  },
  async getBalance ({ commit, state }) {
    try {
      commit('SET_BALANCE_LOADING')
      const { data } = await balanceApi.getBalance(state.card)
      const { balance, found } = data
      if (found) {
        commit('SET_BALANCE_RESULT', {
          balance: parseFloat(balance),
          cardNotFound: false
        })
      } else {
        commit('SET_BALANCE_RESULT', {
          balance: 0,
          cardNotFound: true
        })
      }
      return found
    } catch (error) {
      commit('SET_BALANCE_RESULT', {
        balance: 0,
        cardNotFound: true
      })
      return false
    }
  }
}

const getters = {
  cardNotFound: (state) => state.cardNotFound,
  balanceIsLoading: (state) => state.loadingBalance,
  loadedAndWrong: (state) => {
    return state.card && state.card.length === 12 && state.cardNotFound === true && state.balanceLoaded
  },
  loadedAndCorrect: (state) => {
    return state.card && state.card.length === 12 && state.cardNotFound === false && state.balanceLoaded
  },
  cardBrand: (state) => {
    if (state.commissionBrand === 'MASTER CARD') {
      return 'MC'
    }

    return state.commissionBrand
  },
  isInternational: (state) => {
    return state.commissionType === 'international'
  },
  cardHolderName: (state) => {
    return `${state.cardHolderFirstName} ${state.cardHolderLastName}`
  },
  creditType: (state) => {
    return (state.cardType === 0) ? 'DB' : 'CR'
  },
  cardExpirationDate: (state) => {
    return `${state.cardExpirationMonth}/${state.cardExpirationYear.slice(-2)}`
  },
  commissionPrice: (state, getters, rootState, rootGetters) => {
    const amount = parseFloat(state.amount)
    const commissionAmount = parseFloat(state.commissionAmount)

    if (commissionAmount > 0 && amount > 0) {
      const valueBeforeTax = amount * commissionAmount
      const commissionPrice = valueBeforeTax + (valueBeforeTax * 0.16)

      return commissionPrice
    }

    return null
  },
  formattedAmount: (state) => {
    if (state.amount > 0) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'MXN',
        currencyDisplay: 'symbol'
      }).format(state.amount)
    }

    return null
  },
  formattedCommissionPrice: (state, getters) => {
    const commissionPrice = getters.commissionPrice

    if (commissionPrice) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'MXN',
        currencyDisplay: 'symbol'
      }).format(commissionPrice)
    }

    return null
  },
  formattedBalance: (state) => {
    const currentBalance = parseFloat(state.balance)

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MXN',
      currencyDisplay: 'symbol'
    }).format(currentBalance)
  },
  formattedNewBalance: (state, getters, rootState, rootGetters) => {
    const currentBalance = parseFloat(state.balance)

    if (state.amount > 0) {
      const newBalance = currentBalance + state.amount

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'MXN',
        currencyDisplay: 'symbol'
      }).format(newBalance)
    }

    return null
  },
  formattedTotal: (state, getters, rootState, rootGetters) => {
    const amount = parseFloat(state.amount)
    const commissionPrice = getters.commissionPrice

    if (amount > 0 && commissionPrice) {
      const total = parseFloat(amount) + parseFloat(commissionPrice)

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'MXN',
        currencyDisplay: 'symbol'
      }).format(total)
    }

    return null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
